import { assertCanMergeDatasets, type UserRole } from "@/app/userRole";
import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

/**
 * 데이터셋 통합이다. N개 공용 Dataset 의 콘텐츠를 합쳐 새 Dataset 하나를 만든다.
 *
 * 통합본은 자기 저장 위치에 자기 바이트를 가진 스냅샷이다. 그래서 생성 후에는 소스가 바뀌어도
 * 영향을 받지 않는다. 복사가 GB 단위라서 요청을 큐에 넣고(202) 잡을 폴링한다.
 * 원본 N개는 통합 후에도 그대로 남는다. 보관(archive) 액션으로 따로 정리한다.
 *
 * 호환 판정(채널 구조·embodiment 등)은 서버가 소유한다. FE 는 목록 행 정보로 미리 막지 않는다.
 * FE 는 `409 dataset.merge_incompatible` 사유를 그대로 보여준다.
 */

// 통합 대상 수의 범위다. 1건은 통합이 아니다. 상한은 한 잡이 옮기는 바이트 양을 제한한다.
export const datasetMergeSourceMin = 2;
export const datasetMergeSourceMax = 50;

// 통합 요청이다. 대상 목록과 결과 Dataset 표시명을 담는다.
export type DatasetMergeReq = {
  source_dataset_uuids: string[];
  display_name: string;
};

/**
 * 통합 잡 lifecycle 이다. 통합은 전부 성공하거나 전부 실패한다. 그래서 부분 실패 상태가 없다.
 * 취소를 만드는 producer 가 없어서 `cancelled` 도 없다.
 */
export type DatasetMergeJobStatus = "queued" | "running" | "succeeded" | "failed";

/** 통합 잡 상태다. 결과 Dataset uuid 는 성공했을 때만 값이 있다. */
export type DatasetMergeJobResp = {
  uuid: string;
  status: DatasetMergeJobStatus;
  source_count: number;
  // 복사 진행률이다(object 단위, 분모/분자). 워커가 claim 하기 전이면 둘 다 null 이다.
  requested_count: number | null;
  copied_count: number | null;
  // 성공하면 만들어진 통합 Dataset 이다. 진행 중이거나 실패했으면 null 이다.
  merged_dataset_uuid: string | null;
  // 실패 사유 코드다(비동기 실패는 `conflict` · `merge_failed` · `merge_job.reclaim_exhausted`).
  // 그 외 상태면 null 이다.
  error_code: string | null;
};

/**
 * 복사 진행률(0..100)을 계산한다. 계획이 아직 없거나(분모 null·0) 분자가 없으면 null 이다.
 *
 * 이 값은 object 단위 비율이다. 그래서 사용자에게 퍼센트만 표시하고 개수는 표시하지 않는다.
 * 한 에피소드는 여러 파일이므로 object 수는 사용자가 아는 에피소드 수와 다르다.
 */
export function datasetMergeProgressPercent(job: DatasetMergeJobResp): number | null {
  const { requested_count: requested, copied_count: copied } = job;
  if (requested === null || requested <= 0 || copied === null) {
    return null;
  }
  return Math.min(100, Math.floor((copied / requested) * 100));
}

/** 잡이 아직 끝나지 않았는지 판정한다. 호출부는 이 값으로 폴링을 계속할지 정한다. */
export function isDatasetMergeJobActive(status: DatasetMergeJobStatus): boolean {
  return status === "queued" || status === "running";
}

/** 통합을 큐에 넣는다(202, 조직 멤버). 호출부는 응답의 잡을 그대로 폴링한다. */
export type DatasetMergeMutationOptions = {
  role: UserRole | null;
};

export async function enqueueDatasetMerge(
  payload: DatasetMergeReq,
  options: DatasetMergeMutationOptions,
): Promise<DatasetMergeJobResp> {
  assertCanMergeDatasets(options.role);
  const response = await apiClient.post<DatasetMergeJobResp>(apiRoutes.dataset.merge, payload);
  return response.data;
}

/** 통합 잡의 진행 상태를 조회한다(폴링). */
export async function getDatasetMergeJob(jobUuid: string): Promise<DatasetMergeJobResp> {
  const response = await apiClient.get<DatasetMergeJobResp>(apiRoutes.mergeJob.detail(jobUuid));
  return response.data;
}
